import { BusinessObjectBase } from './base';
import { supportCommentSchema } from '@schemas/business-objects/support-comment';
import type { Account } from './account';
import type { SupportAttachment } from './support-attachment';
import type { SupportCase } from './support-case';
import type { User } from './user';
import type { Id, Markdown } from '../base-types';

export interface SupportCommentData {
  readonly id: number;
  readonly body?: Markdown;
  readonly isSystem?: boolean;
  readonly caseId: number;
  readonly case?: SupportCase;
  readonly accountId: number;
  readonly account?: Account;
  readonly attachments?: SupportAttachment[];
  readonly createdAt?: Date;
  readonly createdById?: Id;
  readonly updatedAt?: Date;
  readonly updatedById?: Id;
  readonly createdBy?: User;
  readonly updatedBy?: User;
}

export interface SupportComment extends Readonly<SupportCommentData> {}
type SupportCommentCreateWithChildrenData = Partial<SupportCommentData>;

export class SupportComment extends BusinessObjectBase {
  static readonly entityName = 'SupportComment' as const;
  static readonly plural = 'support-comments';
  static schema = supportCommentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SupportComment[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SupportComment(data));
  }

  static async findOne(filter?: any): Promise<SupportComment | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SupportComment(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<SupportComment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SupportComment(data);
  }

  static async create(
    data: Omit<SupportCommentData, 'id'>,
  ): Promise<SupportComment> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SupportComment(result);
  }

  static async createMany(
    data: Omit<SupportCommentData, 'id'>[],
  ): Promise<SupportComment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SupportComment(item));
  }

  static async createWithChildren(
    data: SupportCommentCreateWithChildrenData,
  ): Promise<SupportComment> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new SupportComment(result);
  }

  static async createManyWithChildren(
    data: SupportCommentCreateWithChildrenData[],
  ): Promise<SupportComment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SupportComment(item));
  }

  static async updateById(
    id: number,
    updates: Partial<SupportCommentData>,
  ): Promise<SupportComment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new SupportComment(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
